import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Check as CheckIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { Customer, ItsmConfig } from '../../../types';
import { extractErrorWithDetail } from '../../../shared/utils/errorUtils';
import FreshserviceEntityPicker, {
  FreshserviceEntity,
} from '../../../shared/components/FreshserviceEntityPicker';
import { useCustomerService, ItsmConfigTestResult } from '../services/customerService';

interface CustomerItsmSectionProps {
  customer: Customer;
  onSaved: (updated: Customer) => void;
}

type IdField = 'company_id' | 'default_group_id' | 'default_responder_id' | 'requester_id';

interface FormState {
  company_id: number | null;
  default_group_id: number | null;
  default_responder_id: number | null;
  requester_id: number | null;
  requester_email: string;
}

const toForm = (cfg: ItsmConfig | null | undefined): FormState => ({
  company_id: cfg?.company_id ?? null,
  default_group_id: cfg?.default_group_id ?? null,
  default_responder_id: cfg?.default_responder_id ?? null,
  requester_id: cfg?.requester_id ?? null,
  requester_email: cfg?.requester_email ?? '',
});

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CustomerItsmSection: React.FC<CustomerItsmSectionProps> = ({ customer, onSaved }) => {
  const service = useCustomerService();
  const [form, setForm] = useState<FormState>(toForm(customer.itsm_config));
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<ItsmConfigTestResult | null>(null);
  const [companyError, setCompanyError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  const [companies, setCompanies] = useState<FreshserviceEntity[]>([]);
  const [groups, setGroups] = useState<FreshserviceEntity[]>([]);
  const [agents, setAgents] = useState<FreshserviceEntity[]>([]);
  const [loadingLists, setLoadingLists] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);

  useEffect(() => {
    setForm(toForm(customer.itsm_config));
    setTestResult(null);
    setSaveError(null);
    setCompanyError(null);
    setEmailError(null);
  }, [customer.id, customer.itsm_config]);

  // Companies, groups, and agents are all tenant-global — load on mount.
  // (OpsPilot raises tickets as its own agent, so the requester is an agent, not
  // a company-scoped contact.)
  useEffect(() => {
    let cancelled = false;
    setLoadingLists(true);
    setLookupError(null);
    Promise.all([
      service.listFreshserviceCompanies(customer.id),
      service.listFreshserviceGroups(customer.id),
      service.listFreshserviceAgents(customer.id),
    ])
      .then(([c, g, a]) => {
        if (cancelled) return;
        setCompanies(c);
        setGroups(g);
        setAgents(a);
      })
      .catch((err) => {
        if (cancelled) return;
        const { message, detail } = extractErrorWithDetail(err);
        setLookupError(detail ? `${message}: ${detail}` : message);
      })
      .finally(() => {
        if (!cancelled) setLoadingLists(false);
      });
    return () => {
      cancelled = true;
    };
  }, [customer.id, service]);

  const setIdField = (field: IdField) => (id: number | null) => {
    setForm((prev) => ({ ...prev, [field]: id }));
    if (field === 'company_id') setCompanyError(null);
    setSaveError(null);
    setTestResult(null);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, requester_email: e.target.value }));
    setEmailError(null);
    setSaveError(null);
    setTestResult(null);
  };

  const buildPayload = (): { config: ItsmConfig | null; valid: boolean } => {
    const email = form.requester_email.trim();
    const allEmpty =
      form.company_id == null &&
      form.default_group_id == null &&
      form.default_responder_id == null &&
      form.requester_id == null &&
      !email;
    if (allEmpty) {
      setCompanyError(null);
      setEmailError(null);
      return { config: null, valid: true };
    }

    let valid = true;
    if (form.company_id == null) {
      setCompanyError('Select a Company when other ITSM fields are set');
      valid = false;
    }
    if (email && !EMAIL_RE.test(email)) {
      setEmailError('Invalid email format');
      valid = false;
    }
    if (!valid) return { config: null, valid: false };

    const config: ItsmConfig = { company_id: form.company_id as number };
    if (form.default_group_id != null) config.default_group_id = form.default_group_id;
    if (form.default_responder_id != null) config.default_responder_id = form.default_responder_id;
    if (form.requester_id != null) config.requester_id = form.requester_id;
    if (email) config.requester_email = email;
    return { config, valid: true };
  };

  const handleSave = async () => {
    const { config, valid } = buildPayload();
    if (!valid) return;
    setSaving(true);
    setSaveError(null);
    setTestResult(null);
    try {
      const updated = await service.updateItsmConfig(customer.id, config);
      onSaved(updated);
    } catch (err) {
      const { message, detail } = extractErrorWithDetail(err);
      setSaveError(detail ? `${message}: ${detail}` : message);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await service.testItsmConfig(customer.id);
      setTestResult(result);
    } catch (err) {
      const { message, detail } = extractErrorWithDetail(err);
      setTestResult({ success: false, message: detail ? `${message}: ${detail}` : message });
    } finally {
      setTesting(false);
    }
  };

  const configured = !!customer.itsm_config;
  const isDirty = useMemo(() => {
    const saved = toForm(customer.itsm_config);
    return (
      form.company_id !== saved.company_id ||
      form.default_group_id !== saved.default_group_id ||
      form.default_responder_id !== saved.default_responder_id ||
      form.requester_id !== saved.requester_id ||
      form.requester_email !== saved.requester_email
    );
  }, [form, customer.itsm_config]);
  const testDisabled = !configured || testing || saving || isDirty;
  const testDisabledReason = !configured
    ? 'Save the ITSM config before testing'
    : isDirty
      ? 'You have unsaved changes — save first to test the new values'
      : '';

  return (
    <Accordion sx={{ mt: 3 }} defaultExpanded={false}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
          <Typography variant="subtitle1">ITSM integration</Typography>
          {configured ? (
            <Chip size="small" color="success" label="Configured" />
          ) : (
            <Chip size="small" color="default" label="Not configured" />
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Routes ITSM tickets created for this customer. The Company is sent as{' '}
            <code>department_id</code> on the wire (Freshservice MSP legacy naming for Companies).
          </Typography>

          {lookupError && (
            <Alert severity="warning">Couldn't load Freshservice lists: {lookupError}</Alert>
          )}

          <FreshserviceEntityPicker
            label="Company *"
            options={companies}
            value={form.company_id}
            onChange={setIdField('company_id')}
            loading={loadingLists}
            error={!!companyError}
            helperText={companyError ?? 'The Freshservice Company (sent as department_id).'}
            required
          />

          <FreshserviceEntityPicker
            label="Default group"
            options={groups}
            value={form.default_group_id}
            onChange={setIdField('default_group_id')}
            loading={loadingLists}
            helperText="Optional. Default agent group tickets are routed to."
          />

          <FreshserviceEntityPicker
            label="Default assignee (agent)"
            options={agents}
            value={form.default_responder_id}
            onChange={setIdField('default_responder_id')}
            loading={loadingLists}
            helperText="Optional. The agent tickets are assigned to. Unset leaves them unassigned."
          />

          <FreshserviceEntityPicker
            label="Default requester (agent)"
            options={agents}
            value={form.requester_id}
            onChange={setIdField('requester_id')}
            loading={loadingLists}
            helperText="Optional. The Freshservice agent OpsPilot raises tickets as."
          />

          <TextField
            label="Default Requester Email"
            value={form.requester_email}
            onChange={handleEmailChange}
            error={!!emailError}
            helperText={emailError ?? 'Optional. Fallback when no requester is set.'}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />

          {saveError && <Alert severity="error">{saveError}</Alert>}
          {testResult && (
            <Alert
              severity={testResult.success ? 'success' : 'error'}
              icon={testResult.success ? <CheckIcon /> : <ErrorIcon />}
            >
              {testResult.message}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Tooltip title={testDisabledReason} placement="top">
              <span>
                <Button
                  variant="outlined"
                  onClick={handleTest}
                  disabled={testDisabled}
                  startIcon={testing ? <CircularProgress size={16} /> : <CheckIcon />}
                >
                  Test connection
                </Button>
              </span>
            </Tooltip>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : configured ? 'Save changes' : 'Set ITSM config'}
            </Button>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default CustomerItsmSection;
